import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Two visual styles via the `variant` prop on TabsList:
 *
 *   - "segmented" (default): the shadcn pill-style pill in a muted track.
 *     Good for inline filters / toggles inside a page.
 *   - "underline": flat, underline-on-active. Matches the renewals SubNav.
 *     Good for top-level section nav inside a page (Dashboard | Pipeline |
 *     Data) and for tab strips that carry counts (Orphan contracts 31 | …).
 *
 * The variant is shared via context — pass it on TabsList, all child
 * TabsTriggers pick it up automatically.
 */

type TabsVariant = "segmented" | "underline";

const TabsVariantContext = React.createContext<TabsVariant>("segmented");

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: TabsVariant;
  }
>(({ className, variant = "segmented", ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        variant === "segmented"
          ? "inline-flex h-9 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground"
          : "flex items-center gap-1 border-b border-border",
        className,
      )}
      {...props}
    />
  </TabsVariantContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    /** Optional count to render beside the label as a neutral pill (underline variant only). */
    count?: number | string;
  }
>(({ className, children, count, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        variant === "segmented" &&
          "rounded-sm px-3 py-1 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
        variant === "underline" &&
          "-mb-px rounded-t-md border-b-2 border-transparent px-4 py-2 text-foreground/70 hover:border-border hover:text-foreground data-[state=active]:border-brand data-[state=active]:text-brand",
        className,
      )}
      {...props}
    >
      <span>{children}</span>
      {count != null && variant === "underline" ? (
        <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 text-[11px] font-semibold text-muted-foreground">
          {count}
        </span>
      ) : null}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
